import { apiFetch } from './client';

export type AccountApprovalStatus = 'pending' | 'waitlisted' | 'approved' | 'rejected';
export type AccountRequestTab = 'pending' | 'waitlisted' | 'completed';
export type AccountRequestAction = 'approve' | 'reject' | 'waitlist' | 'pending';

export interface AccountRequest {
  id: number;
  email: string;
  username?: string;
  firstname?: string;
  lastname?: string;
  name?: string;
  mobile?: string;
  date_of_birth?: string | null;
  account_approval_status: AccountApprovalStatus;
  created_at: string;
  updated_at?: string;
  artist_name?: string | null;
  distributor?: string | null;
  spotify_url?: string | null;
  instagram_url?: string | null;
  apple_url?: string | null;
  youtube_url?: string | null;
  tiktok_url?: string | null;
  deezer_url?: string | null;
  amazon_url?: string | null;
}

export interface AccountRequestsPage {
  requests: AccountRequest[];
  total: number;
  limit: number;
  offset: number;
  tab: AccountRequestTab;
}

export interface ArtistAnalyticsMetricChange {
  followersDaily: number | null;
  listenersDaily: number | null;
  popularityWeekly: number | null;
  popularityWeeklyPercent: number | null;
  ratioWeekly: number | null;
  ratioWeeklyPercent: number | null;
}

export interface RequestArtistAnalytics {
  artist: {
    chartmetricId: number | string;
    name: string | null;
    spotifyArtistId: string;
    instagramId: number | string | null;
  };
  spotify: {
    status: 'available';
    profileUrl: string | null;
    followers: number | null;
    monthlyListeners: number | null;
    popularity: number | null;
    followersToListenersRatio: number | null;
    changes: ArtistAnalyticsMetricChange;
    timestamps: Record<string, string | null>;
  };
  instagram: {
    status: 'available' | 'unavailable' | 'restricted';
    reason?: string;
    followers?: number | null;
    engagementRate?: number | null;
    averageLikesPerPost?: number | null;
    averageCommentsPerPost?: number | null;
    timestamp?: string | null;
    topCountries?: Array<{ name: string | null; country: string | null; code: string | null; percent: number | null; count: number | null }>;
    topCities?: Array<{ name: string | null; country: string | null; code: string | null; percent: number | null; count: number | null }>;
    audienceGenders?: Array<{ label: string | null; percent: number | null }>;
    audienceAges?: Array<{ range: string | null; malePercent: number | null; femalePercent: number | null; totalPercent: number | null }>;
    topInterests?: Array<{ name: string | null; percent: number | null; affinity: number | null }>;
    notableFollowers?: Array<{ username: string | null; fullName: string | null; followers: number | null; verified: boolean; profileUrl: string | null }>;
  };
  fetchedAt: string;
}

const artistAnalyticsCache = new Map<number, Promise<RequestArtistAnalytics>>();

export const listAccountRequests = async (params: {
  tab: AccountRequestTab;
  limit?: number;
  offset?: number;
}): Promise<AccountRequestsPage> => {
  const q = new URLSearchParams();
  q.set('tab', params.tab);
  if (params.limit != null) q.set('limit', String(params.limit));
  if (params.offset != null) q.set('offset', String(params.offset));
  const response = await apiFetch(`/admin/account-requests?${q.toString()}`, { method: 'GET' });
  return response.data;
};

export const updateAccountRequest = async (
  userId: number,
  body: Partial<{
    firstname: string;
    lastname: string;
    mobile: string;
    date_of_birth: string;
    artist_name: string;
    distributor: string;
    spotify_url: string;
    instagram_url: string;
    apple_url: string;
    youtube_url: string;
    tiktok_url: string;
    deezer_url: string;
    amazon_url: string;
  }>,
): Promise<AccountRequest> => {
  const response = await apiFetch(`/admin/account-requests/${userId}`, {
    method: 'PUT',
    body: JSON.stringify(body),
  });
  artistAnalyticsCache.delete(userId);
  return response.data;
};

export const getRequestArtistAnalytics = async (
  userId: number,
  options: { forceRefresh?: boolean } = {},
): Promise<RequestArtistAnalytics> => {
  if (options.forceRefresh) artistAnalyticsCache.delete(userId);
  const cached = artistAnalyticsCache.get(userId);
  if (cached) return cached;

  const request = apiFetch(`/admin/account-requests/${userId}/artist-analytics`, {
    method: 'GET',
    skipArtistId: true,
  }).then((response) => response.data as RequestArtistAnalytics);
  artistAnalyticsCache.set(userId, request);
  request.catch(() => artistAnalyticsCache.delete(userId));
  return request;
};

export const decideAccountRequest = async (
  userId: number,
  action: AccountRequestAction,
): Promise<AccountRequest> => {
  const response = await apiFetch(`/admin/account-requests/${userId}/${action}`, {
    method: 'POST',
    body: JSON.stringify({}),
  });
  return response.data;
};
